import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

export interface ChangelogEntry {
  id: string;
  slug: string | null;
  title: string;
  published_at: string; // YYYY-MM-DD (display)
  created_at: string; // ISO display time
  released_at?: string | null; // ISO, when readers could first see it
  tags: string[];
  body: string; // Markdown
  media_url: string | null;
  media_type: "image" | "video" | null;
}

export interface ChangelogResponse {
  success: boolean;
  data: ChangelogEntry[];
  total: number;
  // This reader has something released since their last look (the
  // package's unread rule, computed by the backend over every entry the
  // reader may see).
  unread: boolean;
  // The most recently released entry: what "seen" stamps.
  latest_id: string | null;
  latest_published_at: string | null;
}

/**
 * "What's New" for the signed-in reader (central DB, @mediatechgroup/changelog),
 * newest first, only the entries this reader's audience may see. `unread`
 * decides whether to auto-open; `latest_id` is what markChangelogSeen stamps.
 */
export const getChangelog = (
  token: string | null,
  opts: { limit?: number; offset?: number } = {}
): Promise<ChangelogResponse> =>
  axios
    .get(`${BASE_URL}/changelog`, {
      params: {
        ...(opts.limit ? { limit: opts.limit } : {}),
        ...(opts.offset ? { offset: opts.offset } : {}),
      },
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });

/**
 * Stamp the reader's What's New read state: they have now seen everything up
 * to `latestId`. Resolves false on failure (reported), never throws: a failed
 * stamp only means the modal may open once more.
 */
export const markChangelogSeen = (token: string | null, latestId: string | null): Promise<boolean> =>
  axios
    .post(
      `${BASE_URL}/changelog/seen`,
      { latest_id: latestId },
      { headers: { authorization: `Bearer ${token}` } }
    )
    .then(() => true)
    .catch((err) => {
      captureException(err);
      return false;
    });
